import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { MagnifyingGlassPlus, X } from "@phosphor-icons/react";
import { useLanguage } from "@/context/LanguageContext";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GallerySection() {
  const { t } = useLanguage();
  const [gallery, setGallery] = useState([]);
  const [filter, setFilter] = useState("all");
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    axios.get(`${API}/gallery`).then(res => setGallery(res.data)).catch(() => {});
  }, []);

  const items = gallery;
  const filtered = filter === "all" ? items : items.filter(i => i.category === filter);

  const filters = [
    { label: t.gallery.filters.all, value: "all" },
    { label: t.gallery.filters.capitone, value: "capitone" },
    { label: t.gallery.filters.acanalado, value: "acanalado" },
    { label: t.gallery.filters.liso, value: "liso" },
  ];

  return (
    <section id="galeria" className="py-24 md:py-32 bg-[#050505]" data-testid="gallery-section">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-[#FFD700] mb-4 font-light">{t.gallery.overline}</p>
            <h2 className="font-['Cormorant_Garamond'] text-3xl md:text-5xl font-light text-white tracking-tight">{t.gallery.title}</h2>
          </div>
          <div className="flex gap-3" data-testid="gallery-filters">
            {filters.map((f) => (
              <button key={f.value} onClick={() => setFilter(f.value)} className={`px-4 py-2 text-xs uppercase tracking-wider transition-all duration-300 border ${filter === f.value ? "bg-[#FFD700] text-black border-[#FFD700]" : "bg-transparent text-zinc-400 border-white/10 hover:border-[#FFD700] hover:text-[#FFD700]"}`} data-testid={`gallery-filter-${f.value}`}>{f.label}</button>
            ))}
          </div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((item, i) => (
            <motion.div key={item.id} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.1 }} className={`group bg-[#121212] border border-white/10 overflow-hidden cursor-pointer ${i === 0 ? "md:col-span-2 lg:col-span-2" : ""}`} onClick={() => setLightbox(item)} data-testid={`gallery-card-${item.id}`}>
              <div className="relative h-72 md:h-80 overflow-hidden">
                <img src={item.image} alt={item.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#121212]/80 to-transparent" />
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-black/30">
                  <MagnifyingGlassPlus size={36} weight="bold" className="text-[#FFD700]" />
                </div>
                <div className="absolute top-4 left-4">
                  <span className="px-3 py-1 text-xs uppercase tracking-wider bg-[#FFD700] text-black">{t.gallery.categories[item.category] || item.category}</span>
                </div>
              </div>
              <div className="p-6">
                <h3 className="font-['Cormorant_Garamond'] text-xl text-white mb-2">{item.title}</h3>
                <p className="text-sm text-zinc-400 font-light">{item.description}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {lightbox && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4" onClick={() => setLightbox(null)} data-testid="gallery-lightbox">
          <button onClick={() => setLightbox(null)} className="absolute top-6 right-6 text-white/70 hover:text-[#FFD700] transition-colors z-10" data-testid="lightbox-close"><X size={32} weight="bold" /></button>
          <div className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
            <img src={lightbox.image} alt={lightbox.title} className="w-full max-h-[80vh] object-contain" />
            <div className="mt-4 text-center">
              <h3 className="font-['Cormorant_Garamond'] text-2xl text-white">{lightbox.title}</h3>
              <p className="text-zinc-400 text-sm mt-1">{lightbox.description}</p>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
